import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { brand } from "../../../theme/businessFlowsTheme";
import colourForType from "../../../utils/typeColours";
import { attributeLabel, typeLabel, withDefaults } from "../../../utils/viewConfig";
import { canEditDatabase, contentDatabases, startingDatabase } from "../../../utils/permissions";
import {
  getAttributeMapping,
  listAttributeMapping,
  removeAttributeMapping,
  saveAttributeMapping,
} from "../../../actions";
import DatabaseSelect from "./DatabaseSelect";

// An attribute reads here exactly as it reads on a card or a fact sheet.
const label = attributeLabel;

// Which attributes the properties panel shows for each object type, and in what
// order. Held per database. viewTypes: the database's object types as Views
// resolved them, so a type shows the admin's name and colour here too.
export default function PropertiesPanelSettings({ user = {}, database: fixedDatabase, embedded = false, initialType = "", viewTypes = null }) {
  const viewConfig = useMemo(() => withDefaults({ types: viewTypes || [] }), [viewTypes]);
  // Staff: every database; customers: the ones they edit.
  const databases = useMemo(() => contentDatabases(user), [user]);
  const labelFor = useCallback((name) => user.databaseLabels?.[name] || name, [user.databaseLabels]);

  const [ownDatabase, setDatabase] = useState(() => startingDatabase(user, databases));
  // Inside Views the database is chosen there.
  const database = fixedDatabase || ownDatabase;
  const mayManage = canEditDatabase(user, database);
  const [types, setTypes] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [detail, setDetail] = useState(null);
  const [shown, setShown] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadTypes = useCallback(
    async (keepSelection) => {
      if (!database) return;
      setTypes(null);
      const res = await listAttributeMapping(database);
      if (res?.success) {
        setTypes(res.content);
        if (!keepSelection) {
          const wanted = initialType && res.content.some((row) => row.type === initialType) ? initialType : res.content[0]?.type || "";
          setSelectedType(wanted);
        }
      } else {
        setTypes([]);
        setError(res?.msg || "Couldn't read the configuration for this database.");
      }
    },
    [database, initialType]
  );

  useEffect(() => {
    setError("");
    setQuery("");
    setDetail(null);
    loadTypes(false);
  }, [loadTypes]);

  const loadDetail = useCallback(async () => {
    if (!database || !selectedType) return;
    setDetail(null);
    const res = await getAttributeMapping(database, selectedType);
    if (res?.success) {
      setDetail(res.content);
      setShown(res.content.assignedAttributes.map(({ attribute, showAlways }) => ({ attribute, showAlways })));
    } else setError(res?.msg || "Couldn't read this type's attributes.");
  }, [database, selectedType]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const filteredTypes = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (types || []).filter((row) => !term || row.type.toLowerCase().includes(term));
  }, [types, query]);

  const original = useMemo(
    () => (detail?.assignedAttributes || []).map(({ attribute, showAlways }) => ({ attribute, showAlways })),
    [detail]
  );
  const dirty = JSON.stringify(original) !== JSON.stringify(shown);

  const move = (index, step) => {
    const next = [...shown];
    const target = index + step;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setShown(next);
  };

  const save = async () => {
    setBusy("save");
    setError("");
    const res = await saveAttributeMapping(database, selectedType, shown);
    setBusy("");
    if (res?.success) {
      setNotice(res.msg || "Saved.");
      await Promise.all([loadTypes(true), loadDetail()]);
    } else setError(res?.msg || "Couldn't save the mapping.");
  };

  const remove = async () => {
    setBusy("remove");
    setError("");
    const res = await removeAttributeMapping(database, selectedType);
    setBusy("");
    if (res?.success) {
      setNotice(res.msg || "Mapping removed.");
      await Promise.all([loadTypes(true), loadDetail()]);
    } else setError(res?.msg || "Couldn't remove the mapping.");
  };

  const available = (detail?.available || []).filter((row) => !shown.some((item) => item.attribute === row.attribute));

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {!embedded && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal }}>
            <TuneRoundedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Properties panel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose which attributes users see for each type, and the order they read in.
            </Typography>
          </Box>
          <DatabaseSelect databases={databases} labelFor={labelFor} value={database} onChange={setDatabase} />
        </Stack>
      )}

      {!mayManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can review this database's settings. Changing them needs Edit rights: ask your workspace admin.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "320px minmax(0, 1fr)" }, alignItems: "start" }}>
        <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, overflow: "hidden" }}>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Find a type"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 520, overflowY: "auto" }}>
            {types === null ? (
              <Box sx={{ p: 2 }}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} height={40} />
                ))}
              </Box>
            ) : filteredTypes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
                No types match.
              </Typography>
            ) : (
              <Stack divider={<Divider />}>
                {filteredTypes.map((row) => (
                  <Box
                    key={row.type}
                    onClick={() => setSelectedType(row.type)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      cursor: "pointer",
                      borderLeft: `4px solid ${row.type === selectedType ? brand.signal : "transparent"}`,
                      bgcolor: row.type === selectedType ? brand.canvas : "transparent",
                      "&:hover": { bgcolor: brand.canvas },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colourForType(row.type, viewConfig), flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                          {typeLabel(viewConfig, row.type)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.objects} {row.objects === 1 ? "object" : "objects"}
                          {row.mapped ? ` · ${row.attributes} shown` : " · shows everything"}
                        </Typography>
                      </Box>
                      {!row.mapped && <Chip size="small" label="Not set" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }} />}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Paper>

        <Stack spacing={2}>
          {!selectedType ? (
            <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Choose a type to set up its properties panel.
              </Typography>
            </Paper>
          ) : detail === null ? (
            <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, p: 3 }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} height={44} />
              ))}
            </Paper>
          ) : (
            <>
              <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, p: { xs: 2, md: 2.5 } }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colourForType(selectedType, viewConfig), mt: 0.75 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>{typeLabel(viewConfig, selectedType)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {detail.mapped
                        ? `${shown.length} ${shown.length === 1 ? "attribute" : "attributes"} in the panel, top to bottom.`
                        : "No mapping yet — users see every attribute these objects carry. Add attributes to control that."}
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={1} sx={{ mt: 2 }}>
                  {shown.length === 0 ? (
                    <Box sx={{ p: 2.5, borderRadius: 3, border: `1px dashed ${brand.line}`, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Nothing added yet. Pick attributes from the list below.
                      </Typography>
                    </Box>
                  ) : (
                    shown.map((item, index) => {
                      const usage = detail.available
                        .concat(detail.assignedAttributes)
                        .find((row) => row.attribute === item.attribute);
                      return (
                        <Stack
                          key={item.attribute}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ p: 1, pl: 1.5, borderRadius: 3, border: `1px solid ${brand.line}`, bgcolor: "#fff" }}
                        >
                          <Typography variant="caption" sx={{ width: 20, color: "text.secondary", fontWeight: 700 }}>
                            {index + 1}
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: "capitalize" }}>
                              {label(item.attribute)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {usage ? `${usage.filled} ${usage.filled === 1 ? "object has" : "objects have"} a value` : "not on any object yet"}
                            </Typography>
                          </Box>
                          <Tooltip title="Show even when empty">
                            <Switch
                              size="small"
                              checked={item.showAlways}
                              disabled={!mayManage}
                              onChange={(e) =>
                                setShown(shown.map((row, i) => (i === index ? { ...row, showAlways: e.target.checked } : row)))
                              }
                            />
                          </Tooltip>
                          <Stack direction="row">
                            <IconButton size="small" disabled={!mayManage || index === 0} onClick={() => move(index, -1)} aria-label="Move up">
                              <ArrowUpwardRoundedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={!mayManage || index === shown.length - 1}
                              onClick={() => move(index, 1)}
                              aria-label="Move down"
                            >
                              <ArrowDownwardRoundedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={!mayManage}
                              onClick={() => setShown(shown.filter((_, i) => i !== index))}
                              aria-label={`Remove ${item.attribute}`}
                            >
                              <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      );
                    })
                  )}
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    disabled={!mayManage || !dirty || shown.length === 0 || busy === "save"}
                    startIcon={busy === "save" ? <CircularProgress size={16} color="inherit" /> : null}
                    onClick={save}
                  >
                    Save panel
                  </Button>
                  <Button disabled={!dirty} startIcon={<RestartAltRoundedIcon />} onClick={() => setShown(original)}>
                    Undo changes
                  </Button>
                  {detail.mapped && (
                    <Button
                      color="error"
                      disabled={!mayManage || busy === "remove"}
                      startIcon={<DeleteOutlineRoundedIcon />}
                      onClick={remove}
                      sx={{ ml: { sm: "auto" } }}
                    >
                      Remove mapping
                    </Button>
                  )}
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, p: { xs: 2, md: 2.5 } }}>
                <Typography sx={{ fontWeight: 700 }}>Attributes on these objects</Typography>
                <Typography variant="body2" color="text.secondary">
                  Found on {selectedType} objects in {labelFor(database)}. Add the ones users should read.
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  {available.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Every attribute these objects carry is already in the panel.
                    </Typography>
                  ) : (
                    available.map((row) => (
                      <Chip
                        key={row.attribute}
                        label={`${label(row.attribute)} · ${row.filled}`}
                        icon={<AddRoundedIcon />}
                        disabled={!mayManage}
                        onClick={() => setShown([...shown, { attribute: row.attribute, showAlways: false }])}
                        sx={{ borderRadius: 2, fontWeight: 600, textTransform: "capitalize" }}
                      />
                    ))
                  )}
                </Stack>
              </Paper>
            </>
          )}
        </Stack>
      </Box>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setNotice("")} sx={{ borderRadius: 3 }}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
}
